import { darken } from "polished";
import styled, { css, StyledProps } from "styled-components";
import {
  colorMapper,
  colorMapperWithoutProps,
  devices,
  mapper,
  sizeMapper,
} from "src/styles";
import { COLOR_SECONDARY } from "src/styles/colors";
import { Link } from "react-router-dom";
import { Icon } from "src/components";

const getButtonColor = (props: StyledButtonProps) =>
  props.color === COLOR_SECONDARY ? props.theme.grayDarker : "#fff";

export const BareButtonStyle = css`
  position: relative;
  display: inline-block;
  padding: 0;
  background-color: transparent;
  border: none;
  cursor: pointer;
  outline: none;
  text-align: center;
  color: inherit;
  user-select: none;
`;

export type ButtonProps = Partial<{
  borderColor?: string;
  borderRadius?: string;
  color: string;
  disabled: boolean;
  fluid: boolean;
  fullWidth: boolean;
  hoverColor: string;
  line: boolean;
  margin: string;
  marginTop: string;
  noCapitalize: boolean;
  noWrap: boolean;
  onClick: any;
  padding: string;
  size: string;
  text: boolean;
  underline: boolean;
  loadingIndicator: boolean;
  noBorder?: boolean;
}>;

type StyledButtonProps = StyledProps<ButtonProps>;

export const Button = styled.button<ButtonProps>`
  ${BareButtonStyle};
  margin: ${(props) => sizeMapper(props.margin, "no")};
  color: ${getButtonColor};
  background-color: ${colorMapper};
  border-radius: ${(props) => props.borderRadius || "5px"};

  padding: ${(props) =>
    props.padding
      ? sizeMapper(props.padding)
      : mapper(
          props.size,
          {
            xxxSmall: "0.25rem 0.5rem", // 4px 8px
            xxSmall: "0.5rem 1rem", // 8px 16px
            xSmall: "0.6rem 1.2rem", // 9px 18px
            small: "0.8rem 1.6rem", // 12px 24px
            medium: "0.8rem 2.5rem", // 12px 40px
            large: "1rem 2.5rem", // 16px 40px
            xLarge: "1.2rem 3rem", // 18px 48px
            xxLarge: "1.5rem 3.6rem", // 24px 56px
            xxxLarge: "1.7rem 4rem", // 27px 64px
            extraLarge: "2.5rem 5rem", // 40px 80px
          },
          "xSmall"
        )};

  line-height: ${(props) =>
    mapper(
      props.size,
      {
        xxxSmall: "0.4rem", // 6px
        xxSmall: "0.5rem", // 8px
        xSmall: "0.6rem", // 9px
        small: "0.8rem", // 12px
        medium: "0.9rem", // 14px
        large: "1rem", // 16px
        xLarge: "1.2rem", // 18px
        xxLarge: "1.5rem", // 24px
        xxxLarge: "1.7rem", // 27px
        extraLarge: "2.5rem", // 40px
      },
      "medium"
    )};
  font-size: ${(props) =>
    mapper(
      props.size,
      {
        xxxSmall: "0.4rem", // 6px
        xxSmall: "0.5rem", // 8px
        xSmall: "0.6rem", // 9px
        small: "0.8rem", // 12px
        medium: "0.9rem", // 14px
        large: "1rem", // 16px
        xLarge: "1.2rem", // 18px
        xxLarge: "1.5rem", // 24px
        xxxLarge: "1.7rem", // 27px
        extraLarge: "2.5rem", // 40px
      },
      "medium"
    )};

  &:active {
    background-color: ${(props) => darken(0.05, colorMapper(props))};
  }

  &:hover {
    ${(props) =>
      !props.text &&
      css`
        box-shadow: rgba(50, 50, 93, 0.25) 0px 6px 12px -2px,
          rgba(0, 0, 0, 0.3) 0px 3px 7px -3px;
      `}
  }

  &:disabled {
    opacity: 0.5;
    cursor: default;

    &:hover {
      box-shadow: none;
    }

    &:active {
      background-color: ${colorMapper};
    }
  }

  ${(props) =>
    props.fullWidth &&
    css`
      width: 100%;
    `};

  ${(props) =>
    props.fluid &&
    css`
      flex: 1;
    `};

  ${(props) =>
    props.line &&
    css`
      background-color: transparent;
      border: 1px solid
        ${props.borderColor
          ? colorMapperWithoutProps(props.borderColor)
          : colorMapper};
      color: ${colorMapper};

      &:active {
        background-color: transparent;
        border-color: ${(props) => darken(0.05, colorMapper(props))};
        color: ${(props) => darken(0.05, colorMapper(props))};
      }
    `};

  ${(props) =>
    props.text &&
    css`
      padding: ${mapper(props.padding, {
        no: "0",
        small: "4px 0",
        medium: "8px 0",
        large: "12px 0",
      })};
      background-color: transparent;
      color: ${colorMapper};

      &:active {
        background-color: transparent;
        color: ${(props) => darken(0.05, colorMapper(props))};
      }
    `};

  ${(props) =>
    props.noWrap &&
    css`
      white-space: nowrap;
    `}

  ${(props) =>
    props.underline &&
    css`
      text-decoration: underline;
    `}

    //fontSize increase by 1.3 for tablet
  ${devices.tablet} {
    font-size: ${(props) =>
      mapper(
        props.size,
        {
          xxxSmall: "0.4rem", // 6px
          xxSmall: "0.5rem", // 8px
          xSmall: "0.7rem", // 11px
          small: "0.8rem", // 12px
          medium: "0.9rem", // 14px
          large: "1rem", // 16px
          xLarge: "1.2rem", // 19px
          xxLarge: "1.4rem", // 22px
          xxxLarge: "1.5rem", // 24px
          extraLarge: "3.5rem", // 56px
        },
        "medium"
      )};
  }

  ${devices.desktop} {
    font-size: ${(props) =>
      mapper(
        props.size,
        {
          xxxSmall: "0.5rem", // 8px
          xxSmall: "0.688rem", // 11px
          xSmall: "0.75rem", // 12px
          small: "0.875rem", // 14px
          medium: "1rem", // 16px
          large: "1.5rem", // 24px
          xLarge: "2rem", // 32px
          xxLarge: "2.25rem", // 36px
          xxxLarge: "4.5", // 72px
        },
        "medium"
      )};
  }

  ${(props) =>
    props.loadingIndicator &&
    css`
      position: relative;
      color: transparent !important;
      pointer-events: none;
      opacity: 0.5;
      cursor: default;

      &:after {
        content: "";
        animation: shadowRolling 2s linear infinite;
        border-radius: 50%;
        display: block;
        height: 10px;
        color: #fff;
        position: absolute;
        top: 50%;
        transform: translateY(-50%);
        width: 10px;
      }

      @keyframes shadowRolling {
        0% {
          box-shadow: 0px 0 rgba(255, 255, 255, 0), 0px 0 rgba(255, 255, 255, 0),
            0px 0 rgba(255, 255, 255, 0), 0px 0 rgba(255, 255, 255, 0);
        }
        12% {
          box-shadow: 100px 0 currentColor, 0px 0 rgba(255, 255, 255, 0),
            0px 0 rgba(255, 255, 255, 0), 0px 0 rgba(255, 255, 255, 0);
        }
        25% {
          box-shadow: 110px 0 currentColor, 100px 0 currentColor,
            0px 0 rgba(255, 255, 255, 0), 0px 0 rgba(255, 255, 255, 0);
        }
        36% {
          box-shadow: 120px 0 currentColor, 110px 0 currentColor,
            100px 0 currentColor, 0px 0 rgba(255, 255, 255, 0);
        }
        50% {
          box-shadow: 130px 0 currentColor, 120px 0 currentColor,
            110px 0 currentColor, 100px 0 currentColor;
        }
        62% {
          box-shadow: 200px 0 rgba(255, 255, 255, 0), 130px 0 currentColor,
            120px 0 currentColor, 110px 0 currentColor;
        }
        75% {
          box-shadow: 200px 0 rgba(255, 255, 255, 0),
            200px 0 rgba(255, 255, 255, 0), 130px 0 currentColor,
            120px 0 currentColor;
        }
        87% {
          box-shadow: 200px 0 rgba(255, 255, 255, 0),
            200px 0 rgba(255, 255, 255, 0), 200px 0 rgba(255, 255, 255, 0),
            130px 0 currentColor;
        }
        100% {
          box-shadow: 200px 0 rgba(255, 255, 255, 0),
            200px 0 rgba(255, 255, 255, 0), 200px 0 rgba(255, 255, 255, 0),
            200px 0 rgba(255, 255, 255, 0);
        }
      }
    `};
`;

export const ButtonLink = Button.withComponent(
  ({ borderColor, margin, size, color, fluid, line, text, ...props }) => (
    <Link decoration="none" {...props} />
  )
);

export const IconButton = styled.button<ButtonProps>`
  ${BareButtonStyle}
  border-radius: .375rem;
  border: 1px solid ${(props) => colorMapper(props)};
  color: ${(props) => colorMapper(props)};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: ${(props) =>
    props.padding ? sizeMapper(props.padding) : "0.375rem 0.4rem"};
  margin: ${(props) => (props.margin ? sizeMapper(props.margin) : "0")};
  font-size: 0.75rem;
  font-weight: 500;

  ${(props) =>
    props.noBorder &&
    css`
      border: none;
    `}

  ${(props) =>
    props.disabled &&
    css`
      opacity: 0.7;
      cursor: default;
      svg,
      span {
        opacity: 0.6;
      }
    `}
`;

export type IconButtonIconProps = {
  color?: string;
  size?: string;
  noRightMargin?: boolean;
};

export const IconButtonIcon = styled(Icon)<IconButtonIconProps>`
  vertical-align: middle;
  margin-right: ${(props) => (props.noRightMargin ? "0" : "0.5rem")};
  width: ${(props) =>
    mapper(
      props.size,
      {
        xSmall: "8px",
        small: "10px",
        medium: "15px",
        large: "20px",
        mLarge: "25px",
        xLarge: "30px",
        xxLarge: "35px",
      },
      "medium"
    )};

  height: ${(props) =>
    mapper(
      props.size,
      {
        xSmall: "8px",
        small: "10px",
        medium: "15px",
        large: "20px",
        mLarge: "25px",
        xLarge: "30px",
        xxLarge: "35px",
      },
      "medium"
    )};

  ${(props) =>
    props.color &&
    css`
      color: ${colorMapper(props)};
    `};
`;
